import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { ImageUpload } from "./ImageUpload";

export function EntityManager({ collection, title, subtitle, fields }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const titleField = fields[0].name;
  const subField = fields.find((f) => f.type !== "image" && f.name !== titleField)?.name;
  const imageField = fields.find((f) => f.type === "image")?.name;

  const load = () =>
    api
      .get(`/content/${collection}`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error(`Failed to load ${title}`));

  useEffect(() => {
    setItems(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const emptyForm = () => {
    const f = {};
    fields.forEach((fd) => {
      f[fd.name] = fd.type === "boolean" ? false : fd.type === "number" ? 0 : "";
    });
    return f;
  };

  const openEdit = (item) => {
    const f = { id: item.id };
    fields.forEach((fd) => {
      f[fd.name] = item[fd.name] ?? (fd.type === "boolean" ? false : fd.type === "number" ? 0 : "");
    });
    setForm(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {};
    fields.forEach((fd) => (payload[fd.name] = form[fd.name]));
    try {
      if (form.id) {
        await api.put(`/admin/content/${collection}/${form.id}`, payload);
      } else {
        await api.post(`/admin/content/${collection}`, payload);
      }
      toast.success("Saved");
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[titleField]}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/content/${collection}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const inputCls = "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-pool";

  return (
    <div data-testid={`admin-${collection}`}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <button
          onClick={() => setForm(emptyForm())}
          className="inline-flex items-center gap-2 bg-slate-950 hover:bg-pool text-white text-[11px] tracking-wider uppercase font-semibold px-5 py-3 transition-colors"
          data-testid={`admin-${collection}-add`}
        >
          <Plus className="h-4 w-4" /> Add new
        </button>
      </div>

      {items === null ? (
        <p className="text-sm text-slate-400 py-10">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-400 py-10 border border-dashed border-slate-300 text-center">
          Nothing here yet — add your first entry.
        </p>
      ) : (
        <div className="bg-white border border-slate-200 divide-y divide-slate-100" data-testid={`admin-${collection}-list`}>
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-4 px-4 sm:px-6 py-4">
              {imageField && (
                <div className="h-14 w-14 shrink-0 bg-slate-100 overflow-hidden">
                  {item[imageField] ? (
                    <img src={imgSrc(item[imageField])} alt="" className="h-full w-full object-cover" />
                  ) : null}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-slate-900 truncate">{item[titleField]}</p>
                {subField && (
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {typeof item[subField] === "boolean" ? (item[subField] ? "Yes" : "No") : item[subField]}
                  </p>
                )}
              </div>
              <button
                onClick={() => openEdit(item)}
                className="p-2.5 text-slate-400 hover:text-pool transition-colors"
                aria-label="Edit"
                data-testid={`admin-${collection}-edit-${item.id}`}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                onClick={() => remove(item)}
                className="p-2.5 text-slate-400 hover:text-red-500 transition-colors"
                aria-label="Delete"
                data-testid={`admin-${collection}-delete-${item.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid={`admin-${collection}-form-modal`}>
          <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setForm(null)} />
          <form
            onSubmit={save}
            className="relative z-10 w-full max-w-xl bg-white p-6 sm:p-8 my-8"
            data-testid={`admin-${collection}-form`}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-xl text-slate-900">
                {form.id ? "Edit" : "Add"} {title.replace(/s$/, "")}
              </h3>
              <button type="button" onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-900" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {fields.map((fd) => (
                <div key={fd.name}>
                  {fd.type === "boolean" ? (
                    <label className="flex items-center gap-3 text-sm font-medium text-slate-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!form[fd.name]}
                        onChange={(e) => setForm({ ...form, [fd.name]: e.target.checked })}
                        className="h-4 w-4 accent-cyan-500"
                        data-testid={`admin-field-${fd.name}`}
                      />
                      {fd.label}
                    </label>
                  ) : (
                    <>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                        {fd.label}
                        {fd.required ? " *" : ""}
                      </label>
                      {fd.type === "textarea" ? (
                        <textarea
                          rows={4}
                          required={fd.required}
                          value={form[fd.name]}
                          onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })}
                          className={inputCls}
                          data-testid={`admin-field-${fd.name}`}
                        />
                      ) : fd.type === "select" ? (
                        <select
                          value={form[fd.name]}
                          onChange={(e) => setForm({ ...form, [fd.name]: e.target.value })}
                          className={inputCls}
                          data-testid={`admin-field-${fd.name}`}
                        >
                          <option value="">Select…</option>
                          {fd.options.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </select>
                      ) : fd.type === "image" ? (
                        <ImageUpload
                          value={form[fd.name]}
                          onChange={(v) => setForm({ ...form, [fd.name]: v })}
                          testId={`admin-field-${fd.name}`}
                        />
                      ) : (
                        <input
                          type={fd.type === "number" ? "number" : "text"}
                          required={fd.required}
                          value={form[fd.name]}
                          onChange={(e) =>
                            setForm({ ...form, [fd.name]: fd.type === "number" ? Number(e.target.value) : e.target.value })
                          }
                          className={inputCls}
                          data-testid={`admin-field-${fd.name}`}
                        />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 flex gap-3">
              <button
                type="submit"
                disabled={busy}
                className="bg-slate-950 hover:bg-pool disabled:opacity-60 text-white text-[11px] tracking-wider uppercase font-semibold px-7 py-3.5 transition-colors"
                data-testid={`admin-${collection}-save`}
              >
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => setForm(null)}
                className="border border-slate-300 text-slate-600 text-[11px] tracking-wider uppercase font-semibold px-7 py-3.5 hover:border-slate-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
